import React from 'react';
import {Graph} from '../../../../../components/graph/components/graph';
import {GraphEdge, GraphNode} from '../../../../../components/graph/interface';
import {useNeighborhoodGraphProps} from './hooks/use-neighborhood-graph-props';
import {BrowserNotSupportedView} from '../../../../../components/graph/components/browser-not-supported-view';
import {
    CustomResizableLayout,
    ResizablePanel
} from '../../../../../components/layout/resizable-layout';
import {useGraphSelectionProps} from '../../../../../components/graph/graph-logic/use-graph-selection-props';
import style from './neighborhood-graph.module.css';
import {FactsetPanel} from './factset-panel';
import {useNeighborhoodGraphSettings} from './hooks/use-neighborhood-graph-settings';
import {useDocLayoutSettings} from '../../hooks/use-doc-layout-settings';

interface Props {
    documentId: string;
    selectedFacts: string[];
    globalVersionId: number;
    nodes: GraphNode[];
    edges: GraphEdge[];
    graphSettings: ReturnType<typeof useNeighborhoodGraphSettings>;
    graphSelectionProps: ReturnType<typeof useGraphSelectionProps>;
    layoutSettings: ReturnType<typeof useDocLayoutSettings>;
    canFetchNodesStructure: boolean;
}

export function NeighborhoodGraphLoaded(props: Props) {
    const graphProps = useNeighborhoodGraphProps({nodes: props.nodes, edges: props.edges});
    const clearGraphSelection = React.useCallback(() => {
        props.graphSelectionProps.setSelectedNodes([]);
    }, [props.graphSelectionProps]);

    return (
        <CustomResizableLayout
            localStorageKey="documents.explore.neighborhoodGraph.factsetPanel.layoutSize"
            alignment="vertical"
            settings={{initialFirstPanelShare: 0.6, minPermittedHeight: 200}}
            fixedPanelSize={props.layoutSettings.factsetPanelFixedSize}
        >
            {(resizableLayoutProps) => (
                <React.Fragment>
                    <ResizablePanel
                        index={0}
                        className={style.neighborhoodGraphResizablePanel}
                        {...resizableLayoutProps}
                    >
                        {(function () {
                            if (
                                props.layoutSettings.isGraphPanelCollapsed ||
                                !graphProps.nodePositions
                            ) {
                                return undefined;
                            }

                            if (graphProps.isBrowserUnsupported) {
                                return <BrowserNotSupportedView />;
                            }

                            return (
                                <Graph
                                    nodePositions={graphProps.nodePositions}
                                    handleNodeMoved={graphProps.handleNodeMoved}
                                    nodes={props.nodes}
                                    edges={props.edges}
                                    selectedNodes={props.graphSelectionProps.selectedNodes}
                                    selectNodes={props.graphSelectionProps.onNodesSelected}
                                    hoveredNodes={props.graphSelectionProps.hoveredNodes}
                                    setHoveredNodes={props.graphSelectionProps.setHoveredNodes}
                                    zoom={graphProps.zoom}
                                    setZoom={graphProps.setZoom}
                                    pan={graphProps.pan}
                                    setPan={graphProps.setPan}
                                    hiddenNodes={[]}
                                />
                            );
                        })()}
                    </ResizablePanel>

                    {props.graphSelectionProps.selectedNodes.length ? (
                        <ResizablePanel
                            index={1}
                            className={style.factsetPanel}
                            {...resizableLayoutProps}
                        >
                            <FactsetPanel
                                documentId={props.documentId}
                                selectedFacts={props.selectedFacts}
                                globalVersionId={props.globalVersionId}
                                selectedNodeIds={props.graphSelectionProps.selectedNodes}
                                graphSettings={props.graphSettings}
                                closePanel={clearGraphSelection}
                                isCollapsed={props.layoutSettings.isFactsetPanelCollapsed}
                                toggleIsCollapsed={props.layoutSettings.toggleFactsetPanelCollapsed}
                                canFetchNodesStructure={props.canFetchNodesStructure}
                            />
                        </ResizablePanel>
                    ) : undefined}
                </React.Fragment>
            )}
        </CustomResizableLayout>
    );
}
